import { Segment, toDateTimeStr, useGraphCtx } from '@mi-front/core';
import { theme } from '@mi-front/ui';
import { useTheme } from '@mui/material';
import { WaveformEl } from '../waveform/base/WaveformEl';
import { WaveformRenderer } from '../waveform/base/WaveformRenderer';
import NoAudio from '../waveform/base/message/NoAudio';
import { processSilentSegments } from '../waveform/silence-chunks-utils';
// const _ = require('lodash');

interface Props {
  readonly segments: readonly Segment[];
  readonly fgColor?: string;
  readonly bgColor?: string;
  readonly debug?: boolean;
}

/**
 * Splits an rgba/rgb color string into its opaque RGB component and alpha.
 * Applying opacity at the container <g> level instead of on each path fill
 * prevents opacity accumulation where adjacent paths bleed into each other,
 * which would otherwise produce a brighter seam at every segment boundary.
 */
function parseColorOpacity(color: string): { opaqueColor: string; opacity: number } {
  const m = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/);
  if (m) {
    return {
      opaqueColor: `rgb(${m[1]},${m[2]},${m[3]})`,
      opacity: m[4] !== undefined ? parseFloat(m[4]) : 1,
    };
  }
  return { opaqueColor: color, opacity: 1 };
}

export function SegmentsWaveform(p: Props) {
  const { segments, fgColor, bgColor, debug } = p;
  const { height, scale, compressed } = useGraphCtx();
  const muiTheme = useTheme();
  const defaultFgColor = muiTheme.palette.mode === 'dark' ? 'rgba(28,192,252,0.65)' : theme.blue;

  const processed = processSilentSegments(segments);

  const effectiveFgColor = fgColor || defaultFgColor;
  const { opaqueColor, opacity } = parseColorOpacity(effectiveFgColor);

  const xs = processed.map((seg) => Math.round(scale(seg.startTs)!));
  const lastEndX =
    processed.length > 0 ? Math.round(scale(processed[processed.length - 1].endTs)!) : 0;

  return (
    <g className="segments-waveform" opacity={opacity}>
      {processed.map((segment, index: number) => {
        const x = xs[index];
        const ownEndX = Math.round(scale(segment.endTs)!);
        const nextX = index + 1 < xs.length ? xs[index + 1] : lastEndX;
        // Bridge rounding gaps: if nextX is at most 1px ahead of ownEndX (or behind,
        // meaning overlap), extend to nextX. Only leave a visible gap when the pixel
        // distance is > 1, which indicates a real discontinuity in the audio coverage.
        const endX = nextX - ownEndX > 1 ? ownEndX : nextX;
        const width = Math.max(0, endX - x);

        return (
          <WaveformEl
            debug={debug && `${toDateTimeStr(segment.startTs)} (${index})`}
            key={segment.id}
            startTs={segment.startTs}
            duration={segment.duration}
            x={x}
            width={width}
            height={height}
            bgColor={bgColor}
          >
            {segment.waveform ? (
              <WaveformRenderer
                waveform={segment.waveform}
                width={width}
                height={height}
                startTs={segment.startTs}
                x={x}
                fgColor={opaqueColor}
                compressed={compressed}
              />
            ) : (
              <NoAudio noText={compressed} parentWidth={width} parentHeight={height} />
            )}
          </WaveformEl>
        );
      })}
    </g>
  );
}
